"use client";

import { useState } from "react";
import { Box, Link, Typography, styled } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LinkIcon from "@mui/icons-material/Link";
import {
  SectionHeader,
  SectionContent,
  RightColumn,
} from "./styles/SharedStyles";
import EditButton from "@components/shared/EditButton";
import EditPortfolio from "../modals/EditPortfolio";
import getPortfolioUrl from "@lib/candidates/getPortfolioUrl";
import type { PortfolioItem as PortfolioEntry } from "@lib/candidates/saveItemToProfile";
import {
  cloudinaryTransform,
  cloudinaryVideoPoster,
  PORTFOLIO_EDIT_THUMB_TRANSFORM,
  PORTFOLIO_VIDEO_POSTER_TRANSFORM,
} from "@lib/cloudinary";

const PortfolioItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontSize: 14,
}));

interface PortfolioProps {
  candidate: { uuid: string; portfolio?: PortfolioEntry[] | null };
}

const Portfolio = ({ candidate }: PortfolioProps) => {
  const [openEdit, setOpenEdit] = useState(false);

  const items = candidate.portfolio || [];

  return (
    <Box>
      <SectionContent>
        <EditButton onEdit={() => setOpenEdit(true)} disabled={false} sx={{ top: 0, right: 0 }} />
        <Box>
          <RightColumn>
            <SectionHeader>
              <Typography variant="h2">Portfolio</Typography>
            </SectionHeader>
            {items.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No portfolio links added yet.
              </Typography>
            )}
            {items.map((item) => (
              <PortfolioItem key={item.id}>
                {item.type === "image" ? (
                  <Link href={item.url} target="_blank" rel="noopener noreferrer">
                    <Box
                      component="img"
                      src={cloudinaryTransform(item.url, PORTFOLIO_EDIT_THUMB_TRANSFORM)}
                      alt={item.title || "Portfolio image"}
                      sx={{
                        width: 36,
                        height: 36,
                        objectFit: "cover",
                        borderRadius: 1,
                      }}
                    />
                  </Link>
                ) : item.type === "video" ? (
                  <Link
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ position: "relative", display: "inline-flex", lineHeight: 0 }}
                  >
                    <Box
                      component="img"
                      src={cloudinaryVideoPoster(item.url, PORTFOLIO_VIDEO_POSTER_TRANSFORM)}
                      alt={item.title || "Portfolio video"}
                      sx={{
                        width: 36,
                        height: 36,
                        objectFit: "cover",
                        borderRadius: 1,
                      }}
                    />
                    <PlayCircleOutlineIcon
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "white",
                        fontSize: 18,
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
                        pointerEvents: "none",
                      }}
                    />
                  </Link>
                ) : item.type === "file" ? (
                  <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                ) : (
                  <LinkIcon fontSize="small" color="action" />
                )}
                <Link
                  href={item.type === "image" || item.type === "video" ? item.url : getPortfolioUrl(item)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: 14 }}
                >
                  {item.title}
                </Link>
              </PortfolioItem>
            ))}
          </RightColumn>
        </Box>
      </SectionContent>
      <EditPortfolio
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        candidate={candidate}
      />
    </Box>
  );
};

export default Portfolio;
